import { api } from "@/api";
import { useQuery } from "@tanstack/react-query";

export interface DashboardQuery {
    desde?: string;
    hasta?: string;
}

export interface LabelValue {
    label: string;
    value: number;
}

export interface DateValue {
    date: string;
    value: number;
}

export interface DateIntValue {
    date: string;
    value: number;
}

function buildParams(q: DashboardQuery) {
    const params: Record<string, string> = {};
    if (q.desde) params.desde = q.desde;
    if (q.hasta) params.hasta = q.hasta;
    return params;
}

const STALE_TIME = 5 * 60 * 1000;

export function useTopProductos(q: DashboardQuery) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "top-productos", q],
        queryFn: async () => (await api.get("dashboard/top-productos", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export function useClientesDistintosDia(q: DashboardQuery) {
    return useQuery<DateIntValue[]>({
        queryKey: ["dashboard", "clientes-distintos-dia", q],
        queryFn: async () => (await api.get("dashboard/clientes-distintos-dia", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export function useClientesNuevosDia(q: DashboardQuery) {
    return useQuery<DateIntValue[]>({
        queryKey: ["dashboard", "clientes-nuevos-dia", q],
        queryFn: async () => (await api.get("dashboard/clientes-nuevos-dia", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export function useProductosVendidosDia(q: DashboardQuery) {
    return useQuery<DateValue[]>({
        queryKey: ["dashboard", "productos-vendidos-dia", q],
        queryFn: async () => (await api.get("dashboard/productos-vendidos-dia", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export function useMontoPorVendedor(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "monto-por-vendedor", q],
        queryFn: async () => (await api.get("dashboard/monto-por-vendedor", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useUtilidadDiaria(q: DashboardQuery, enabled: boolean) {
    return useQuery<DateValue[]>({
        queryKey: ["dashboard", "utilidad-diaria", q],
        queryFn: async () => (await api.get("dashboard/utilidad-diaria", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useTopRubros(q: DashboardQuery) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "top-rubros", q],
        queryFn: async () => (await api.get("dashboard/top-rubros", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export function useNacionalidadClientes(q: DashboardQuery) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "nacionalidad-clientes", q],
        queryFn: async () => (await api.get("dashboard/nacionalidad-clientes", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
    });
}

export interface CreditoContadoDia {
    date: string;
    credito: number;
    contado: number;
}

export function useStockBajo() {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "stock-bajo"],
        queryFn: async () => (await api.get("dashboard/stock-bajo")).data,
        staleTime: STALE_TIME,
    });
}

export function useVentasCreditoContado(q: DashboardQuery, enabled: boolean) {
    return useQuery<CreditoContadoDia[]>({
        queryKey: ["dashboard", "ventas-credito-contado", q],
        queryFn: async () => (await api.get("dashboard/ventas-credito-contado", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useTopClientes(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "top-clientes", q],
        queryFn: async () => (await api.get("dashboard/top-clientes", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useMetodosPago(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "metodos-pago", q],
        queryFn: async () => (await api.get("dashboard/metodos-pago", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useDEAprobadosTipo(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "de-aprobados-tipo", q],
        queryFn: async () => (await api.get("dashboard/de-aprobados-tipo", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useDEEstadoSifen(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "de-estado-sifen", q],
        queryFn: async () => (await api.get("dashboard/de-estado-sifen", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useDEMontoDia(q: DashboardQuery, enabled: boolean) {
    return useQuery<DateValue[]>({
        queryKey: ["dashboard", "de-monto-dia", q],
        queryFn: async () => (await api.get("dashboard/de-monto-dia", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}

export function useDETopClientes(q: DashboardQuery, enabled: boolean) {
    return useQuery<LabelValue[]>({
        queryKey: ["dashboard", "de-top-clientes", q],
        queryFn: async () => (await api.get("dashboard/de-top-clientes", { params: buildParams(q) })).data,
        staleTime: STALE_TIME,
        enabled,
    });
}
